import * as THREE from 'three';
import {
  PARTICLE_PLACEMENT,
  VISUAL_SETTINGS,
  WIND_MAGNITUDE_RANGE,
  WIND_LIMITS,
  normaliseWindMagnitudeRange,
} from '../../constants.js';
import { generateParticleShaders } from '../shaders/Shaders';

export function createParticlePositionTexture(
  camera,
  particleResolution = VISUAL_SETTINGS.particleResolution,
  planetRadius = VISUAL_SETTINGS.planetRadius,
  particlePlacement = PARTICLE_PLACEMENT.visibleCap
) {
  if (particlePlacement === PARTICLE_PLACEMENT.fullSphere) {
    return createFullSphereParticlePositionTexture(particleResolution, planetRadius);
  }

  const cameraPosition = camera.position.clone();
  const cameraDistance = cameraPosition.length();
  const capDirection = cameraPosition.normalize();
  const visibleCapAngle = Math.acos(
    THREE.MathUtils.clamp(planetRadius / cameraDistance, -1, 1)
  );
  const visibleCircleRadius = Math.sin(visibleCapAngle) * planetRadius;
  const visibleCircleMidpoint = capDirection.clone().multiplyScalar(
    Math.cos(visibleCapAngle) * planetRadius
  );
  const viewableSphereCapMidpoint = capDirection.clone().multiplyScalar(planetRadius);
  const { tangent, bitangent } = createBasis(capDirection);

  const particleCount = particleResolution * particleResolution;
  const particlePositionData = new Float32Array(particleCount * 4);
  const point = new THREE.Vector3();

  for (let index = 0; index < particleCount; index += 1) {
    const theta = Math.random() * Math.PI * 2;
    // Match the original build's slight bias toward the center of the visible cap.
    const rho = visibleCircleRadius * Math.pow(Math.random(), 0.7);

    point
      .copy(visibleCircleMidpoint)
      .addScaledVector(tangent, rho * Math.cos(theta))
      .addScaledVector(bitangent, rho * Math.sin(theta))
      .normalize()
      .multiplyScalar(planetRadius);

    particlePositionData[(index * 4) + 0] = (point.x + 1) * 0.5;
    particlePositionData[(index * 4) + 1] = (point.y + 1) * 0.5;
    particlePositionData[(index * 4) + 2] = (point.z + 1) * 0.5;
    particlePositionData[(index * 4) + 3] = 1;
  }

  const particlePositionDataTexture = new THREE.DataTexture(
    particlePositionData,
    particleResolution,
    particleResolution,
    THREE.RGBAFormat,
    THREE.FloatType
  );

  particlePositionDataTexture.minFilter = THREE.NearestFilter;
  particlePositionDataTexture.magFilter = THREE.NearestFilter;
  particlePositionDataTexture.generateMipmaps = false;
  particlePositionDataTexture.needsUpdate = true;

  return {
    particlePositionDataTexture,
    viewableSphereCapMidpoint,
    viewableSphereCapRadius: getVisibleCapRadius(
      viewableSphereCapMidpoint,
      visibleCircleMidpoint,
      tangent,
      visibleCircleRadius
    ),
  };
}

function createFullSphereParticlePositionTexture(particleResolution, planetRadius) {
  // VR uses a reduced-resolution, all-globe particle texture for Quest performance.
  // The velocity values, colour map, and units still come from the same data textures.
  const particleCount = particleResolution * particleResolution;
  const particlePositionData = new Float32Array(particleCount * 4);
  const point = new THREE.Vector3();

  for (let index = 0; index < particleCount; index += 1) {
    const theta = Math.random() * Math.PI * 2;
    const y = (Math.random() * 2) - 1;
    const radius = Math.sqrt(Math.max(1 - (y * y), 0));

    point
      .set(radius * Math.cos(theta), y, radius * Math.sin(theta))
      .multiplyScalar(planetRadius);

    particlePositionData[(index * 4) + 0] = (point.x + 1) * 0.5;
    particlePositionData[(index * 4) + 1] = (point.y + 1) * 0.5;
    particlePositionData[(index * 4) + 2] = (point.z + 1) * 0.5;
    particlePositionData[(index * 4) + 3] = 1;
  }

  const particlePositionDataTexture = new THREE.DataTexture(
    particlePositionData,
    particleResolution,
    particleResolution,
    THREE.RGBAFormat,
    THREE.FloatType
  );

  particlePositionDataTexture.minFilter = THREE.NearestFilter;
  particlePositionDataTexture.magFilter = THREE.NearestFilter;
  particlePositionDataTexture.generateMipmaps = false;
  particlePositionDataTexture.needsUpdate = true;

  return {
    particlePositionDataTexture,
    viewableSphereCapMidpoint: new THREE.Vector3(0, 0, 0),
    viewableSphereCapRadius: Math.PI,
  };
}

function createBasis(direction) {
  const fallbackUp = Math.abs(direction.y) > 0.95
    ? new THREE.Vector3(1, 0, 0)
    : new THREE.Vector3(0, 1, 0);
  const tangent = new THREE.Vector3().crossVectors(fallbackUp, direction).normalize();
  const bitangent = new THREE.Vector3().crossVectors(direction, tangent).normalize();

  return { tangent, bitangent };
}

export function generateMidpointUVs(size) {
  const uvs = [];

  for (let row = 0; row < size; row += 1) {
    for (let column = 0; column < size; column += 1) {
      uvs.push((column + 0.5) / size, (row + 0.5) / size);
    }
  }

  return new Float32Array(uvs);
}

export function generateParticleMesh(
  particlePositionDataTexture,
  gradientTexture,
  particleResolution,
  velocityTexture,
  windMagnitudeRange = WIND_MAGNITUDE_RANGE
) {
  const particleCount = particleResolution * particleResolution;
  const particleGeometry = new THREE.BufferGeometry();
  const resolvedWindMagnitudeRange = normaliseWindMagnitudeRange(windMagnitudeRange);

  particleGeometry.setAttribute(
    'position',
    new THREE.BufferAttribute(new Float32Array(particleCount * 3), 3)
  );
  particleGeometry.setAttribute(
    'u_squares',
    new THREE.Float32BufferAttribute(generateMidpointUVs(particleResolution), 2)
  );

  const { particleVertexShader, particleFragmentShader } = generateParticleShaders();
  const particleMaterial = new THREE.ShaderMaterial({
    vertexShader: particleVertexShader,
    fragmentShader: particleFragmentShader,
    uniforms: {
      u_positionTexture: { value: particlePositionDataTexture },
      u_velocityTexture: { value: velocityTexture },
      u_minMagVelocity: { value: resolvedWindMagnitudeRange.min },
      u_maxMagVelocity: { value: resolvedWindMagnitudeRange.max },
      u_minLonVelocity: { value: WIND_LIMITS.minLon },
      u_maxLonVelocity: { value: WIND_LIMITS.maxLon },
      u_minLatVelocity: { value: WIND_LIMITS.minLat },
      u_maxLatVelocity: { value: WIND_LIMITS.maxLat },
      u_velocityGradientTexture: { value: gradientTexture },
    },
    blending: THREE.AdditiveBlending,
    transparent: true,
    depthWrite: false,
  });

  const particleMesh = new THREE.Points(particleGeometry, particleMaterial);
  particleMesh.frustumCulled = false;

  return particleMesh;
}

export function updateParticlePositions(
  camera,
  particleResolution,
  planetRadius,
  particleMesh,
  particlePositionDataTexture,
  backgroundMesh,
  particlePlacement = PARTICLE_PLACEMENT.visibleCap
) {
  particlePositionDataTexture?.dispose();

  const nextState = createParticlePositionTexture(
    camera,
    particleResolution,
    planetRadius,
    particlePlacement
  );
  particleMesh.material.uniforms.u_positionTexture.value = nextState.particlePositionDataTexture;
  backgroundMesh.material.uniforms.u_originalPositionTexture.value = nextState.particlePositionDataTexture;
  backgroundMesh.material.uniforms.u_resetChance.value = VISUAL_SETTINGS.interactiveResetChance;
  backgroundMesh.material.uniforms.u_viewableCircleRadius.value = nextState.viewableSphereCapRadius;
  backgroundMesh.material.uniforms.u_viewableCircleMidpoint.value = nextState.viewableSphereCapMidpoint;

  return {
    particlePositionDataTexture: nextState.particlePositionDataTexture,
    backgroundMesh,
  };
}

function getVisibleCapRadius(
  viewableSphereCapMidpoint,
  visibleCircleMidpoint,
  tangent,
  visibleCircleRadius
) {
  const pointOnCircleEdge = visibleCircleMidpoint
    .clone()
    .addScaledVector(tangent, visibleCircleRadius);

  return Math.acos(
    THREE.MathUtils.clamp(
      viewableSphereCapMidpoint.clone().normalize().dot(pointOnCircleEdge.normalize()),
      -1,
      1
    )
  );
}
